/**
 * A gate node on a run's canvas (Feature 79 D23, D34): the diamond, its name,
 * and what it did, the branch it took, that none held, or that it was skipped
 * and why. Its condition nodes show which way it went.
 */
import type { LinkageDecisionMode } from '@optum/api-client/types';

import { Handle, Position, type Node, type NodeProps } from '../../ui/flow.js';
import { ELSE } from './conditions.js';
import type { RunGateFlowNode, RunGateState } from './run-flow.js';

export type RunGateNodeData = RunGateFlowNode['data'] & Record<string, unknown>;
export type RunGateNodeType = Node<RunGateNodeData, 'runGate'>;

const ANCHOR = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 'none' };

/** What the gate did, in words; a switch's else is its default. */
export function gateWords(
  state: Pick<RunGateState, 'status' | 'branch' | 'reason'>,
  mode: LinkageDecisionMode = 'if',
): string {
  if (state.status === 'decided') {
    if (!state.branch) return 'no branch held';
    return `took ${state.branch === ELSE && mode === 'switch' ? 'default' : state.branch}`;
  }
  if (state.status === 'skipped') return state.reason ? `skipped · ${state.reason}` : 'skipped';
  return 'waiting';
}

export function RunGateNode({ data }: NodeProps<RunGateNodeType>) {
  const decided = data.status === 'decided';
  const tone = decided ? 'var(--ok)' : 'var(--text-faint)';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        opacity: data.dimmed ? 0.45 : 1,
      }}
    >
      <span
        className="mono"
        style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}
      >
        {data.name}
      </span>
      <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
        <Handle type="target" position={Position.Left} isConnectable={false} style={ANCHOR} />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 7,
            transform: 'rotate(45deg)',
            borderRadius: 5,
            border: `1.5px solid ${tone}`,
            background: decided ? 'var(--ok-tint)' : 'var(--surface-sunken)',
          }}
        />
        <Handle type="source" position={Position.Right} isConnectable={false} style={ANCHOR} />
      </div>
      <span
        style={{
          fontSize: 'var(--text-meta-lg)',
          fontWeight: 'var(--weight-semibold)',
          color: tone,
        }}
      >
        {gateWords(data, data.mode)}
      </span>
    </div>
  );
}
